/**
 * AuthButton — Brand-styled button for auth screens.
 *
 * Variants:
 * - primary:   Indigo→Violet gradient, white text, brand box-shadow
 * - secondary: White surface, muted border, subtle shadow
 * - ghost:     Primary-light bg, primary text
 * - owner:     Dark gradient (0D0B1E→3730A3), white text
 *
 * Sizes: sm (36px), md (44px), lg (52px).
 * Loading state renders a CSS-animated spinner.
 *
 * @param variant   — Visual style variant.
 * @param size      — Height preset.
 * @param fullWidth — Stretch to 100% width.
 * @param loading   — Show spinner and disable button.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface AuthButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'owner';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
}

const VARIANT_STYLES: Record<
  string,
  { background: string; color: string; border: string; boxShadow: string }
> = {
  primary: {
    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
    color: 'white',
    border: 'none',
    boxShadow: '0 4px 14px rgba(79,70,229,0.32)',
  },
  secondary: {
    background: 'var(--surface)',
    color: 'var(--t2)',
    border: '1.5px solid var(--border-2, #CBC8E0)',
    boxShadow: 'var(--shadow-sm)',
  },
  ghost: {
    background: 'var(--p-lt)',
    color: 'var(--p)',
    border: 'none',
    boxShadow: 'none',
  },
  owner: {
    background: 'linear-gradient(135deg, #0D0B1E, #3730A3)',
    color: 'white',
    border: 'none',
    boxShadow: '0 4px 14px rgba(13,11,30,0.32)',
  },
};

const SIZE_MAP = {
  sm: { height: 36, fontSize: 13 },
  md: { height: 44, fontSize: 14 },
  lg: { height: 52, fontSize: 15 },
} as const;

export function AuthButton({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled,
  ...rest
}: AuthButtonProps) {
  const vs = VARIANT_STYLES[variant] ?? VARIANT_STYLES.primary;
  const sz = SIZE_MAP[size];

  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className="font-body font-bold inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all duration-150"
      style={{
        ...vs,
        height: sz.height,
        fontSize: sz.fontSize,
        width: fullWidth ? '100%' : 'auto',
        padding: '0 20px',
        borderRadius: 'var(--radius-md)',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {loading ? <Spinner /> : children}
    </button>
  );
}

/** CSS-animated 16px spinner for loading state */
function Spinner() {
  return (
    <>
      <style>{`@keyframes auth-spin{to{transform:rotate(360deg)}}`}</style>
      <span
        style={{
          width: 16,
          height: 16,
          border: '2.5px solid rgba(255,255,255,0.3)',
          borderTopColor: 'white',
          borderRadius: '50%',
          display: 'inline-block',
          animation: 'auth-spin 0.7s linear infinite',
        }}
      />
    </>
  );
}

export default AuthButton;